import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Sparkles, CheckCircle, XCircle, User } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { useGenerateCampaignEmails } from '@/hooks/useCampaignAI';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Campaign } from '@/services/campaignService';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign;
}

type Step = 'configure' | 'processing' | 'done';

interface TeamMember {
  id: string;
  first_name: string | null;
  last_name: string | null;
  job_title: string | null;
  work_email: string | null;
}

const CampaignGenerateDialog: React.FC<Props> = ({ open, onOpenChange, campaign }) => {
  const { profile } = useAuth();
  const [step, setStep] = useState<Step>('configure');
  const [selectedSenderId, setSelectedSenderId] = useState<string>('');
  const [result, setResult] = useState<{ generated: number; failed: number; mode: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ processed: number; total: number } | null>(null);
  const generateMutation = useGenerateCampaignEmails();

  const { data: teamMembers = [] } = useQuery<TeamMember[]>({
    queryKey: ['team-members-generate', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const { data } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, job_title, work_email')
        .eq('company_id', profile.company_id);
      return (data || []) as TeamMember[];
    },
    enabled: open && !!profile?.company_id,
  });

  useEffect(() => {
    if (open) {
      setStep('configure');
      setResult(null);
      setError(null);
      setProgress(null);
      const savedSenderId = (campaign?.sender_config as any)?.sender_id;
      setSelectedSenderId(savedSenderId || profile?.id || '');
    }
  }, [open, profile?.id]);

  const handleGenerate = () => {
    setStep('processing');
    setError(null);
    setProgress(null);
    generateMutation.mutate(
      {
        campaignId: campaign.id,
        senderId: selectedSenderId || undefined,
        onProgress: (processed, total) => setProgress({ processed, total }),
      },
      {
        onSuccess: (data) => {
          setResult({ generated: data?.generated || 0, failed: data?.failed || 0, mode: data?.mode || 'unknown' });
          setStep('done');
        },
        onError: (err: any) => {
          setError(err.message || 'Generation failed');
          setStep('done');
        },
      }
    );
  };

  const getMemberLabel = (m: TeamMember) => {
    const name = `${m.first_name || ''} ${m.last_name || ''}`.trim() || 'Unnamed';
    return m.job_title ? `${name} — ${m.job_title}` : name;
  };

  return (
    <Dialog open={open} onOpenChange={step === 'processing' ? undefined : onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Generate Emails
          </DialogTitle>
        </DialogHeader>

        <DialogBody>
          {step === 'configure' && (
            <div className="space-y-4">
              {!campaign.template_id && (campaign.recipient_filters as any)?.target === 'from_queue' ? (
                <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3 text-sm text-destructive">
                  <p className="font-medium">No template linked</p>
                  <p className="mt-1 text-xs">This campaign was converted from a mail merge but has no AI template attached. Regenerating will overwrite existing emails with empty content. Please assign a template first via the campaign editor.</p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Generate personalized emails for <strong>{campaign.name}</strong> using{' '}
                  <strong>{campaign.template_id ? 'AI generation' : 'merge-field substitution'}</strong>.
                  Existing queue items will be replaced.
                </p>
              )}

              {teamMembers.length > 1 && (
                <div>
                  <label className="text-sm font-medium mb-2 block flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5" /> Send As
                  </label>
                  <Select value={selectedSenderId} onValueChange={setSelectedSenderId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select sender" />
                    </SelectTrigger>
                    <SelectContent>
                      {teamMembers.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {getMemberLabel(m)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Emails will use this person's identity and signature.
                  </p>
                </div>
              )}
            </div>
          )}

          {step === 'processing' && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-center gap-2 text-sm font-medium">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                {progress ? `Generating emails... ${progress.processed}/${progress.total}` : 'Generating emails...'}
              </div>
              <Progress
                value={progress ? (progress.processed / progress.total) * 100 : undefined}
                className={`w-full ${!progress ? 'animate-pulse' : ''}`}
              />
              <p className="text-center text-xs text-muted-foreground">
                {progress
                  ? `Processing batch — ${progress.total - progress.processed} remaining`
                  : 'This may take a moment depending on the number of recipients.'}
              </p>
            </div>
          )}

          {step === 'done' && (
            <div className="space-y-4 py-4">
              {error ? (
                <div className="flex flex-col items-center gap-3 text-center">
                  <XCircle className="h-10 w-10 text-destructive" />
                  <p className="text-sm font-medium">Generation Failed</p>
                  <p className="text-sm text-muted-foreground">{error}</p>
                </div>
              ) : result && (
                <div className="flex flex-col items-center gap-3 text-center">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                  <p className="text-sm font-medium">Generation Complete</p>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>Mode: <strong>{result.mode === 'ai' ? 'AI Generated' : 'Merge Fields'}</strong></p>
                    <p><span className="text-green-600 font-medium">{result.generated}</span> emails generated</p>
                    {result.failed > 0 && (
                      <p><span className="text-destructive font-medium">{result.failed}</span> failed</p>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    You can now review emails in "View Recipients" and optionally verify them before sending.
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          {step === 'configure' && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                onClick={handleGenerate}
                disabled={!campaign.template_id && (campaign.recipient_filters as any)?.target === 'from_queue'}
              >
                <Sparkles className="h-4 w-4 mr-1.5" />
                Generate
              </Button>
            </>
          )}
          {step === 'done' && (
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CampaignGenerateDialog;
